import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import {
  updateTenant,
  detectEmisorStatus,
  completeOnboarding,
  notifyOwnerAccountantCompleted,
  updateTenantPlan,
  uploadLiteralEProof,
} from "@/services/authService";
import { createPlanCheckout } from "@/services/planPaymentsService";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingProgress } from "@/contexts/OnboardingProgressContext";
import { tenantKeys } from "@/lib/queryKeys";
import {
  getNeedsCfeImportStep,
  getOwnerExtraStepsAfterCert,
  getOwnerPostCertViewSteps,
} from "@/pages/onboarding/ownerPostCertSteps";
import type { SituationChoice } from "@/pages/onboarding/OnboardingSituationStep";
import { isCertBeforeDgi } from "@/pages/onboarding/certFlowOrdering";
import { canLeaveAbitabStep } from "@/pages/onboarding/certificatePhase";
import { isHomologacionPending } from "@/pages/onboarding/homologacionStatus";
import { useOnboardingTenant } from "@/pages/onboarding/useOnboardingTenant";
import { serverMessage } from "@/lib/serverMessage";
import { planUsesActivationPath } from "@/pages/onboarding/planActivation";
import { isFiscalBenefitDeclarationComplete } from "@/components/features/fiscal-benefit/FiscalBenefitDeclaration";
import { useAccountantHandoff } from "@/pages/onboarding/useAccountantHandoff";
import { usePlanGate } from "@/pages/onboarding/usePlanGate";
import { useDgiConfirmation } from "@/pages/onboarding/useDgiConfirmation";
import { useCertificateAcquisition } from "@/pages/onboarding/useCertificateAcquisition";
import { useOnboardingCaeGate } from "@/pages/onboarding/useOnboardingCaeGate";
import { useCfeImportStep } from "@/pages/onboarding/useCfeImportStep";
import { resolveStep } from "@/pages/onboarding/stepMachine/resolveStep";
import { buildStepContext } from "@/pages/onboarding/stepMachine/buildStepContext";
import {
  backStep,
  canAdvance,
  nextButtonLoading as nextButtonLoadingFn,
} from "@/pages/onboarding/stepMachine/transitions";

export function useOnboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const { setProgress } = useOnboardingProgress();

  const [viewStep, setViewStep] = useState<number>(1);
  const [viewStepSynced, setViewStepSynced] = useState(false);
  const [situationChoice, setSituationChoice] = useState<SituationChoice | null>(null);
  const [savingSituation, setSavingSituation] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [savingLegalName, setSavingLegalName] = useState(false);
  const [certificateAcquisitionCanAdvance, setCertificateAcquisitionCanAdvance] =
    useState(false);
  const [step6NotarialCanAdvance, setStep6NotarialCanAdvance] = useState(false);

  useEffect(() => {
    setStep6NotarialCanAdvance(false);
  }, [viewStep]);
  const [certificateAcquisitionChoice, setCertificateAcquisitionChoice] = useState<
    "myself" | "fiscalo" | null
  >(null);
  const loadingRef = useRef(true);
  const prevViewStepRef = useRef<number>(viewStep);

  useEffect(() => {
    prevViewStepRef.current = viewStep;
  }, [viewStep]);

  const tenantBag = useOnboardingTenant({
    onSkipFiscalSuccess: () => setViewStep(5),
  });

  const {
    tenant,
    loading,
    loadTenant,
    saveProfile,
    hasFiscalProfile,
    fiscalStepRef,
    setSavingFiscal,
    setSavingLiteralE,
    setSavingPlan,
    setSavingWhoSetup,
  } = tenantBag;

  // The self path inserts régimen (2) + plan (3) before situation, so situation
  // and every later pre-cert/post-cert step shift by 2. Accountant families keep
  // their integers. Single-sourced here and threaded into the projection
  // (stepToViewStep), the post-cert allocation, the transition ctxs, and the
  // render/forward-nav branches (via the named step integers below).
  const preCertOffset = tenant?.onboardingMode === "self" ? 2 : 0;
  const situationStep = 2 + preCertOffset;
  const dgiStep = 3 + preCertOffset;
  const certChoiceStep = 4 + preCertOffset;
  const certContentStep = 5 + preCertOffset;
  const abitabStep = 6 + preCertOffset;
  // CAE is always the first post-cert step (CERT_STEP + preCertOffset + 1).
  const caeStep = 7 + preCertOffset;

  const {
    dgiPasswordReady,
    setDgiPasswordReady,
    dgiStepsCompletedConfirmed,
    setDgiStepsCompletedConfirmed,
    dgiStep3SuccessToastAcknowledged,
    setDgiStep3SuccessToastAcknowledged,
    dgiConfirmationStatusResolved,
    handleContinueFromDgi,
  } = useDgiConfirmation({
    tenant,
    loadTenant,
    viewStep,
    dgiStep,
    certChoiceStep,
    abitabStep,
    caeStep,
    t,
    situationChoice,
    certificatePath: tenantBag.certificatePath,
    setCertificatePath: tenantBag.setCertificatePath,
    setViewStep,
    setViewStepSynced,
    setCertificateAcquisitionChoice,
  });

  const isOwnerOrAdmin = user?.role === "owner" || user?.role === "admin";
  const isAccountantFlowOwner =
    tenant?.onboardingMode === "accountant" && isOwnerOrAdmin;
  const isAccountantUser = tenant?.onboardingMode === "accountant" && user?.role === "accountant";

  const {
    accountantInviteEmail,
    setAccountantInviteEmail,
    pendingInvites,
    hasAccountantMember,
    finalizeAccountantHandoff,
    handleWhoSetupSubmit,
  } = useAccountantHandoff({
    tenant,
    isAccountantFlowOwner,
    refreshUser,
    navigate,
    t,
    whoSetupChoice: tenantBag.whoSetupChoice,
    setSavingWhoSetup,
    loadTenant,
    setViewStep,
  });

  const {
    currentCertificateRequest,
    currentCertificateRequestLoading,
    certificateRequestPayload,
    setCertificateRequestPayload,
    step6FiscaloConfirmPayload,
    certificateStepNextLoading,
    setCertificateStepNextLoading,
    handleConfirmFiscaloCertificate,
    handleRetryCertificateAutomation,
    certificateAutomationRetrying,
  } = useCertificateAcquisition({
    tenant,
    user,
    viewStep,
    certContentStep,
    abitabStep,
    certificateAcquisitionChoice,
    isAccountantFlowOwner,
    isAccountantUser,
    t,
  });

  const inviteStepCanProceed =
    (pendingInvites?.length ?? 0) > 0 || hasAccountantMember;

  const isAlreadyIssuer =
    situationChoice === "already_issuer" ||
    tenant?.onboardingSituationChoice === "already_issuer";
  const effectiveSituation: "scratch" | "issuer_need_regularization" | "already_issuer" | null =
    situationChoice ?? (tenant?.onboardingSituationChoice ?? null);
  // Two orthogonal concepts (see certFlowOrdering.ts):
  //   - certBeforeDgi (ORDER): the cert phase precedes DGI. Regularization only
  //     — an already-registered emisor can request the cert up front. scratch is
  //     DGI-first (a brand-new emisor has no DGI-authorized identity to sign a
  //     cert for until DGI authorizes it), so its traversal is the linear
  //     2 → 3 → 4 → 5 → 6 → 7 (situación → dgi → certChoice → certContent →
  //     [abitab, fiscalo only] → cae), offset by preCertOffset.
  //   - newCertFlow (SOFT CERT): a fresh cert is being acquired (scratch OR
  //     regularization). The cert may not be in hand at CAE, so the Abitab step
  //     advances on the cert request rather than on credentials.
  const certBeforeDgi = isCertBeforeDgi(effectiveSituation);
  const isScratch = effectiveSituation === "scratch";
  // Self-scratch parks on the "homologación pendiente" screen once the DGI
  // homologación has been started (status set) but not yet completed: the user
  // did the safe self-serve part (created the DGI e-Factura user) and the team
  // finishes homologación. This render gate overrides the normal step, and the
  // wizard polls the tenant so it auto-resumes (cert → CAE) when
  // homologacionStatus reaches completed/vigente. onboardingCompletedAt stays null.
  const needsHomologacionPending =
    isScratch &&
    !tenant?.onboardingCompletedAt &&
    isHomologacionPending(tenant?.homologacionStatus);
  // Poll while parked so the screen auto-advances the moment the team completes
  // homologación (status -> completed/vigente clears the gate).
  useEffect(() => {
    if (!needsHomologacionPending) return;
    const id = setInterval(() => {
      void loadTenant();
    }, 30_000);
    return () => clearInterval(id);
  }, [needsHomologacionPending, loadTenant]);
  const needsCfeImportStep = getNeedsCfeImportStep(
    tenant?.migratingFromAnotherPlatform
  );
  const ownerPostCertViewSteps = useMemo(
    () => getOwnerPostCertViewSteps(needsCfeImportStep, preCertOffset),
    [needsCfeImportStep, preCertOffset]
  );

  const {
    cfeImportUploadCompleted,
    handleSkipCfeImportStep,
    markCfeImportUploadCompleted,
  } = useCfeImportStep({
    tenant,
    viewStep,
    ownerPostCertViewSteps,
    needsCfeImportStep,
    setViewStep,
    loadTenant,
    t,
  });

  const {
    caeListLoading,
    caeListFetching,
    onboardingCaeListRefreshing,
    missingOnboardingCaeCfeTypes,
    hasRequiredOnboardingCaes,
  } = useOnboardingCaeGate({
    tenant,
    viewStep,
    ownerPostCertViewSteps,
    isAccountantFlowOwner,
    isAccountantUser,
  });
  const baseTotalSteps =
    tenant?.onboardingMode == null
      ? 2
      : isAccountantFlowOwner
        ? 7
        : isAccountantUser
          ? 7
          : tenantBag.isMigrating
            ? 11
            : 10;
  const totalSteps =
    isAccountantFlowOwner || isAccountantUser || tenant?.onboardingMode == null
      ? baseTotalSteps
      : baseTotalSteps +
        1 +
        preCertOffset +
        getOwnerExtraStepsAfterCert(needsCfeImportStep);
  const isLastStep = viewStep === totalSteps;

  const {
    needsPlanGate,
    needsRegimeGate,
    handlePlanGateContinue,
    handleRegimeGateContinue,
    persistBenefitDeclarationIfNeeded,
  } = usePlanGate({
    tenant,
    loading,
    viewStep,
    totalSteps,
    isAccountantFlowOwner,
    isAccountantUser,
    selectedPlanId: tenantBag.selectedPlanId,
    selectedBillingInterval: tenantBag.selectedBillingInterval,
    plans: tenantBag.plans,
    benefitDeclaration: tenantBag.benefitDeclaration,
    setSavingPlan,
    setViewStep,
    loadTenant,
    finalizeAccountantHandoff,
    t,
  });

  // Fiscal profile is no longer part of onboarding — users complete it
  // later from Settings. The gate is left as a no-op so other code that
  // references `accountantStepsComplete` doesn't need to change shape.
  const accountantStepsComplete =
    tenantBag.step1Complete &&
    tenant?.literalE !== undefined &&
    tenant?.literalE !== null &&
    Boolean(tenant?.hasDgiCredentials);

  const step = (n: number): boolean => viewStep === n + 1;

  const canFinish =
    isAccountantFlowOwner && step(7)
      ? true
      : isAccountantFlowOwner && step(6)
        ? Boolean(tenant?.planId ?? tenantBag.selectedPlanId)
        : isAccountantUser && step(7)
        ? true
        : isAccountantUser && step(6)
          ? Boolean(tenant?.hasDgiCredentials)
          : !isAccountantUser &&
            tenantBag.step1Complete &&
            Boolean(tenant?.hasDgiCredentials);

  const nextButtonDisabled = canAdvance({
    needsPlanGate,
    needsRegimeGate,
    selectedPlanId: tenantBag.selectedPlanId,
    savingPlan: tenantBag.savingPlan,
    viewStep,
    preCertOffset,
    situationChoice,
    certificateAcquisitionChoice,
    certificateAcquisitionCanAdvance,
    isAccountantFlowOwner,
    isAccountantUser,
    certBeforeDgi,
    newCertFlow: certBeforeDgi || isScratch,
    inviteStepCanProceed,
    accountantStepsComplete,
    savingLegalName,
    step6NotarialCanAdvance,
    certRequestStatus: currentCertificateRequest?.status ?? null,
    certRequestIdDigitalRequestNumber:
      currentCertificateRequest?.idDigitalRequestNumber ?? null,
    tenantStep6AbitabAcknowledgedAt: Boolean(tenant?.step6AbitabAcknowledgedAt),
    tenantCompanyType: tenant?.companyType ?? null,
    tenantHasDgiCredentials: Boolean(tenant?.hasDgiCredentials),
    tenantPlanId: tenant?.planId ?? null,
    ownerPostCertViewSteps,
    caeListFetching,
    hasRequiredOnboardingCaes,
    needsCfeImportStep,
    cfeImportUploadCompleted,
    fiscalStepCanAdvance: tenantBag.fiscalStepCanAdvance,
    savingFiscal: tenantBag.savingFiscal,
    literalE: tenantBag.literalE,
    literalEProofFile: Boolean(tenantBag.literalEProofFile),
    savingLiteralE: tenantBag.savingLiteralE,
    isMigrating: Boolean(tenantBag.isMigrating),
    benefitDeclarationComplete: isFiscalBenefitDeclarationComplete(
      tenantBag.benefitDeclaration,
    ),
  });

  const nextButtonLoading = nextButtonLoadingFn({
    needsPlanGate,
    needsRegimeGate,
    savingPlan: tenantBag.savingPlan,
    viewStep,
    preCertOffset,
    savingSituation,
    isAccountantFlowOwner,
    isAccountantUser,
    certificateStepNextLoading,
    savingFiscal: tenantBag.savingFiscal,
    savingLiteralE: tenantBag.savingLiteralE,
    savingLegalName,
    caeListFetching,
    ownerPostCertViewSteps,
  });

  const hideProgressBar =
    viewStep <= 1 || (isAccountantFlowOwner && viewStep <= 8);

  useEffect(() => {
    if (
      tenant?.onboardingSituationChoice != null &&
      situationChoice === null
    ) {
      setSituationChoice(tenant.onboardingSituationChoice);
    }
  }, [tenant?.onboardingSituationChoice, situationChoice]);

  useEffect(() => {
    setProgress(viewStep, totalSteps, { hideProgressBar });
  }, [viewStep, totalSteps, hideProgressBar, setProgress]);

  useEffect(() => {
    if (viewStep > totalSteps) setViewStep(totalSteps);
  }, [totalSteps, viewStep]);

  // Load-time step reconstruction. The pure `resolveStep` decides the target
  // step (proven equal to the previous inline logic by the golden-master); this
  // effect applies the setState / navigate side-effects it cannot express. The
  // dep array is kept verbatim from the pre-refactor effect to preserve sync
  // timing — resolveStep is a pure function of the ctx built here.
  useEffect(() => {
    const wasLoading = loadingRef.current;
    const stepCtx = buildStepContext({
      loading,
      tenant,
      userRole: user?.role,
      isAccountantFlowOwner,
      isAccountantUser,
      isAlreadyIssuer,
      certBeforeDgi,
      certificateAcquisitionChoice,
      isFiscalProfileSettled: tenantBag.isFiscalProfileSettled,
      certificatePath: tenantBag.certificatePath,
      certificateRequestStatus: currentCertificateRequest?.status ?? null,
      hasAccountantMember,
      dgiStepsCompletedConfirmed,
      dgiStep3SuccessToastAcknowledged,
      caeListLoading,
      hasRequiredOnboardingCaes,
      needsCfeImportStep,
      ownerPostCertViewSteps,
      totalSteps,
      preCertOffset,
      wasLoading,
      prevViewStep: prevViewStepRef.current,
    });
    const result = resolveStep(stepCtx);
    if (stepCtx.loading) {
      loadingRef.current = true;
    } else if (stepCtx.hasTenant) {
      loadingRef.current = false;
    }
    if (result.handoff) {
      void finalizeAccountantHandoff();
      return;
    }
    if (result.navigateDashboard) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (result.choiceSet) {
      setCertificateAcquisitionChoice(result.choice ?? null);
    }
    if (result.step !== undefined) setViewStep(result.step);
    if (result.synced !== undefined) setViewStepSynced(result.synced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loading,
    tenant,
    totalSteps,
    hasFiscalProfile,
    user?.role,
    hasAccountantMember,
    tenantBag.isFiscalProfileSettled,
    tenantBag.certificatePath,
    navigate,
    dgiStepsCompletedConfirmed,
    ownerPostCertViewSteps,
    caeListLoading,
    hasRequiredOnboardingCaes,
    needsCfeImportStep,
    // A refresh starts with the cert-request query cold, so the first pass sees
    // no request and routes to cert_content. Without this dep the effect never
    // runs again once the request arrives and the tenant stays off the Abitab
    // step — i.e. the bug this guard exists to fix, reintroduced through the
    // dep list. The status string (not the object) keeps the effect off
    // identity churn from refetches.
    currentCertificateRequest?.status,
    finalizeAccountantHandoff,
  ]);
  useEffect(() => {
    if (!tenant?.onboardingCompletedAt) return;
    if (isAccountantFlowOwner && viewStep === 8) return;
    navigate("/dashboard", { replace: true });
  }, [tenant?.onboardingCompletedAt, isAccountantFlowOwner, viewStep, navigate]);

  const handleFinish = useCallback(async () => {
    // Two hard gates before we let "Ir al panel" leave /onboarding:
    //   1. Profile filled — backend completeOnboarding requires name + RUT.
    //   2. DGI portal user settled (`hasDgiPortalUser`) — either the
    //      auto-flow submitted Solicitud-Usuario or the user pasted creds
    //      manually. The Python automation's awaiting_delete_user_confirmation
    //      prompt has no UI outside /onboarding, so leaving the page mid-flow
    //      strands the user (prod ran into this — 10-min Python timeouts).
    if (!tenantBag.step1Complete) return;
    if (!tenant?.hasDgiPortalUser) return;
    setFinishing(true);
    try {
      if (isAccountantUser) {
        await notifyOwnerAccountantCompleted().catch(() => {
          // Owner email is best-effort; still go to dashboard
        });
        await refreshUser();
        navigate("/dashboard", { replace: true });
        return;
      }
      if (isAccountantFlowOwner && step(7)) {
        await completeOnboarding();
        await refreshUser();
        await loadTenant();
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        navigate("/dashboard", { replace: true });
        return;
      }
      if (
        isAccountantFlowOwner &&
        step(6) &&
        tenantBag.selectedPlanId &&
        tenant?.planId !== tenantBag.selectedPlanId
      ) {
        await persistBenefitDeclarationIfNeeded();
        if (planUsesActivationPath(
            tenantBag.selectedPlanId,
            tenantBag.selectedBillingInterval,
            tenantBag.plans
          )) {
          const res = await updateTenantPlan(
            tenantBag.selectedPlanId,
            tenantBag.selectedBillingInterval,
            "onboarding"
          );
          await loadTenant();
          if (res.redirectUrl) {
            window.location.href = res.redirectUrl;
            return;
          }
        } else {
          const res = await createPlanCheckout({
            planId: tenantBag.selectedPlanId,
            billingInterval: tenantBag.selectedBillingInterval,
            returnContext: "onboarding",
          });
          await loadTenant();
          if (res.redirectUrl) {
            window.location.href = res.redirectUrl;
            return;
          }
        }
      }
      if (isAccountantFlowOwner && step(6)) {
        await completeOnboarding();
        await refreshUser();
        await loadTenant();
        await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        setViewStep(8);
        return;
      }
      await completeOnboarding();
      await refreshUser();
      await loadTenant();
      await queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
      navigate("/dashboard", { replace: true });
    } catch {
      toast.error(t("onboarding.certificateRequired"));
    } finally {
      setFinishing(false);
    }
  }, [
    tenantBag.step1Complete,
    tenant?.hasDgiPortalUser,
    isAccountantUser,
    isAccountantFlowOwner,
    viewStep,
    step,
    tenant?.planId,
    tenantBag.selectedPlanId,
    tenantBag.selectedBillingInterval,
    refreshUser,
    loadTenant,
    navigate,
    t,
    updateTenantPlan,
    createPlanCheckout,
    completeOnboarding,
  ]);

  /**
   * Marks certificate acquisition finished on the TENANT.
   *
   * Four call sites below reach this at different ends of the Fiscalo path
   * (request pending, request failed, request filed, and the manual
   * acknowledgment). They all used to write the same sessionStorage key
   * inline, which meant the marker lived in one tab: closing it sent the
   * tenant back to a step they had already cleared.
   *
   * Never awaited and never throws. The wizard advances on local state; a lost
   * marker costs a re-answer on the next visit, whereas blocking here would
   * strand someone mid-certificate.
   */
  const markCertAcquisitionDone = useCallback((): void => {
    void updateTenant({
      onboardingCertAcquisitionDoneAt: new Date().toISOString(),
    }).catch(() => {
      // ignore — see above
    });
  }, []);

  /**
   * "I'll keep going while the certificate comes." Same fire-and-forget shape
   * as markCertAcquisitionDone and for the same reason: blocking the button on
   * a PATCH would strand someone on a screen whose whole message is that
   * nothing is being asked of them.
   *
   * A lost write costs more here than there — the tenant is returned to the
   * wait screen on their next visit and presses forward again — but that is
   * the behaviour we would have shipped anyway without this column, so the
   * failure degrades to the safe option rather than to anything broken.
   */
  const markCertWaitContinued = useCallback((): void => {
    void updateTenant({
      onboardingCertWaitContinuedAt: new Date().toISOString(),
    }).catch(() => {
      // ignore — see above
    });
  }, []);

  const handleNextStep = useCallback(async () => {
    if (needsRegimeGate) {
      await handleRegimeGateContinue();
      return;
    }
    if (needsPlanGate) {
      await handlePlanGateContinue();
      return;
    }
    if (viewStep === dgiStep) {
      await handleContinueFromDgi();
      return;
    }
    if (
      viewStep === certChoiceStep &&
      !isAccountantFlowOwner &&
      !isAccountantUser &&
      tenant?.id &&
      certificateAcquisitionChoice
    ) {
      // Persisted on the tenant, not in this tab: the answer to "how do we get
      // your certificate?" should survive a refresh, a new tab and a different
      // device. Not awaited — the wizard advances on the local state either
      // way, and a failed write costs a re-answer rather than a stuck step.
      void updateTenant({
        onboardingCertChoice: certificateAcquisitionChoice,
      }).catch(() => {
        // ignore — see above
      });
      setViewStep(certContentStep);
      return;
    }
    if (
      viewStep === certContentStep &&
      !isAccountantFlowOwner &&
      !isAccountantUser &&
      tenant?.id
    ) {
      if (certificateAcquisitionChoice === "fiscalo") {
        const status = currentCertificateRequest?.status;
        // After the Fiscalo confirmation modal creates the cert request,
        // always land on viewStep 6 so the user sees the "Pasá por
        // Abitab" editorial step with the request number + comprobante.
        // For certBeforeDgi, viewStep 6's handleNextStep then advances
        // to viewStep 3 (DGI auto-flow); for already_issuer it stays the
        // legacy progress + acknowledgment flow.
        const fiscaloNextStep = abitabStep;
        if (status === "pending" || status === "in_progress") {
          void markCertAcquisitionDone();
          setViewStep(fiscaloNextStep);
          return;
        }
        if (status === "failed") {
          void markCertAcquisitionDone();
          setViewStep(fiscaloNextStep);
          return;
        }
        if (!certificateRequestPayload) {
          toast.error(t("onboarding.certificateAcquisition.completeData"));
          return;
        }
        void markCertAcquisitionDone();
        setViewStep(fiscaloNextStep);
        return;
      }
      void markCertAcquisitionDone();
      // Manual ("lo hago yo") path: the user has read the ID Digital
      // instructions and ticked "I understand / I'll get the cert". There's
      // nothing to upload yet (they need to complete the trámite first), so
      // don't drop them into viewStep 6's upload UI — the .pfx is uploaded
      // later from the dashboard's cert prompt. Where they go next depends on
      // the cert ordering:
      //   - regularization (cert-first): on to DGI auto-flow.
      //   - scratch (DGI-first): DGI is already done, so skip the Abitab step
      //     (only meaningful for a Fiscalo-managed cert) and go to CAE.
      //   - already_issuer: the legacy Abitab acknowledgment path.
      if (certBeforeDgi) {
        setViewStep(dgiStep);
      } else if (isScratch) {
        setViewStep(caeStep);
      } else {
        setViewStep(abitabStep);
      }
      return;
    }
    if (viewStep === situationStep) {
      if (situationChoice === null) return;
      setSavingSituation(true);
      try {
        await updateTenant({
          migratingFromAnotherPlatform: false,
          onboardingSituationChoice: situationChoice,
        });
        await loadTenant();
        // regularization: cert-first — kick the cert request off before DGI so
        // the async Abitab identity check runs in parallel with DGI auto-flow.
        // scratch + already_issuer: DGI-first. For scratch this also makes the
        // order deterministic — it agrees with the resolveStep scratch-pin
        // instead of racing it.
        if (situationChoice === "issuer_need_regularization") {
          setViewStep(certChoiceStep);
        } else {
          setViewStep(dgiStep);
        }
      } catch {
        toast.error(t("onboarding.errorSave"));
      } finally {
        setSavingSituation(false);
      }
      return;
    }
    if (isAccountantFlowOwner && step(3)) {
      setViewStep((s) => s + 1);
      return;
    }
    if (isAccountantFlowOwner && step(4)) {
      if (accountantStepsComplete) setViewStep((s) => s + 1);
      return;
    }
    if (isAccountantFlowOwner && step(5)) {
      setSavingLegalName(true);
      try {
        await updateTenant({
          name: tenantBag.legalName.trim() || undefined,
        });
        await loadTenant();
        // Durable for the same reason as the cert choice: the personalize step
        // is done for this TENANT, not for this tab. The updateTenant call
        // above already persisted the name; this marks the step itself.
        try {
          await updateTenant({
            onboardingPersonalizeDoneAt: new Date().toISOString(),
          });
        } catch {
          // ignore — a lost marker costs a re-answer, not a broken step
        }
        setViewStep((s) => s + 1);
        toast.success(t("onboarding.saved"));
      } catch {
        toast.error(t("tenants.edit.errorUpdate"));
      } finally {
        setSavingLegalName(false);
      }
      return;
    }
    if (isAccountantUser && step(6)) {
      setViewStep((s) => s + 1);
      return;
    }
    if (isAccountantFlowOwner && step(6)) {
      if (
        tenantBag.selectedPlanId &&
        tenant?.planId !== tenantBag.selectedPlanId
      ) {
        if (planUsesActivationPath(
            tenantBag.selectedPlanId,
            tenantBag.selectedBillingInterval,
            tenantBag.plans
          )) {
          const res = await updateTenantPlan(
            tenantBag.selectedPlanId,
            tenantBag.selectedBillingInterval,
            "onboarding"
          );
          await loadTenant();
          if (res.redirectUrl) {
            window.location.href = res.redirectUrl;
            return;
          }
        } else {
          setSavingPlan(true);
          try {
            await persistBenefitDeclarationIfNeeded();
            const res = await createPlanCheckout({
              planId: tenantBag.selectedPlanId,
              billingInterval: tenantBag.selectedBillingInterval,
              returnContext: "onboarding",
            });
            await loadTenant();
            if (res.redirectUrl) {
              window.location.href = res.redirectUrl;
              return;
            }
            toast.success(t("onboarding.saved"));
          } catch {
            toast.error(t("onboarding.errorSave"));
          } finally {
            setSavingPlan(false);
          }
          setViewStep((s) => s + 1);
          return;
        }
      }
      setViewStep((s) => s + 1);
      return;
    }
    if (viewStep === abitabStep && !isAccountantFlowOwner && !isAccountantUser) {
      // New-cert (Fiscalo-managed) exit: as soon as the cert request has a
      // request number (status="in_progress"), let the user move on — the
      // Abitab in-person visit then happens *while* the rest configures. The
      // notarial sub-form and step6AbitabAcknowledgedAt acknowledgment stay
      // deferred to the legacy already_issuer path; new-cert users get those
      // reminders on the dashboard after onboarding. Destination depends on
      // ordering: regularization (cert-first) → DGI auto-flow; scratch
      // (DGI-first, DGI already done) → CAE.
      if (certBeforeDgi || isScratch) {
        if (
          currentCertificateRequest &&
          (currentCertificateRequest.status === "in_progress" ||
            currentCertificateRequest.status === "fulfilled" ||
            currentCertificateRequest.status === "pending")
        ) {
          setViewStep(certBeforeDgi ? dgiStep : caeStep);
        }
        return;
      }
      if (tenant?.step6AbitabAcknowledgedAt) {
        // Advance on the cert REQUEST, not on the credentials — the same rule
        // canAdvance applies, stated here because this handler enforces it a
        // second time and the two silently disagreed: the button enabled and
        // then did nothing, because this branch fell straight through its
        // `return` when the .p12 had not arrived. Third place the rule lives
        // (gate, resolver, handler); they now agree.
        if (
          canLeaveAbitabStep({
            acknowledgedAbitab: true,
            certRequestStatus: currentCertificateRequest?.status ?? null,
            hasDgiCredentials: Boolean(tenant?.hasDgiCredentials),
            certificateSkipped: tenant?.dgiCertificateSkippedAt != null,
            waitContinued: tenant?.onboardingCertWaitContinuedAt != null,
          })
        ) {
          setViewStep(ownerPostCertViewSteps.cae);
        }
        return;
      }
      const step6RequiresNotarial = ["sas", "sa", "srl"].includes(
        tenant?.companyType ?? ""
      );
      const step6EmailProcessed = Boolean(
        currentCertificateRequest?.idDigitalRequestNumber
      );
      const step6ShowingNotarialSection =
        step6EmailProcessed && step6RequiresNotarial;
      if (
        step6ShowingNotarialSection &&
        step6NotarialCanAdvance &&
        tenant?.id
      ) {
        setCertificateStepNextLoading(true);
        try {
          await updateTenant({
            step6AbitabAcknowledgedAt: new Date().toISOString(),
          });
          await loadTenant();
          toast.success(t("onboarding.step6AbitabAcknowledgedToast"));
        } catch {
          toast.error(t("onboarding.errorSave"));
        } finally {
          setCertificateStepNextLoading(false);
        }
        return;
      }
      if (tenant?.hasDgiCredentials) setViewStep(ownerPostCertViewSteps.cae);
      return;
    }
    if (
      viewStep === ownerPostCertViewSteps.cae &&
      !isAccountantFlowOwner &&
      !isAccountantUser
    ) {
      // Fiscal profile is no longer part of onboarding — skip from CAE
      // to CFE (if needed) or directly to literalE / personalize.
      setViewStep(
        needsCfeImportStep && ownerPostCertViewSteps.cfe != null
          ? ownerPostCertViewSteps.cfe
          : ownerPostCertViewSteps.literalE !== null
            ? ownerPostCertViewSteps.literalE
            : ownerPostCertViewSteps.personalize
      );
      return;
    }
    if (
      needsCfeImportStep &&
      ownerPostCertViewSteps.cfe != null &&
      viewStep === ownerPostCertViewSteps.cfe &&
      !isAccountantFlowOwner &&
      !isAccountantUser
    ) {
      try {
        await updateTenant({
          onboardingCfeImportStepCompletedAt: new Date().toISOString(),
        });
        await loadTenant();
        // Fiscal profile removed — go directly to literalE / personalize.
        setViewStep(
          ownerPostCertViewSteps.literalE !== null
            ? ownerPostCertViewSteps.literalE
            : ownerPostCertViewSteps.personalize,
        );
      } catch {
        toast.error(t("onboarding.errorSave"));
      }
      return;
    }
    if (viewStep === ownerPostCertViewSteps.fiscal && !isAccountantFlowOwner && !isAccountantUser) {
      setSavingFiscal(true);
      try {
        await fiscalStepRef.current?.save();
        setViewStep(
          ownerPostCertViewSteps.literalE !== null
            ? ownerPostCertViewSteps.literalE
            : ownerPostCertViewSteps.personalize
        );
        toast.success(t("onboarding.saved"));
      } catch {
        toast.error(t("settings.fiscalProfile.error"));
      } finally {
        setSavingFiscal(false);
      }
      return;
    }
    if (
      ownerPostCertViewSteps.literalE !== null &&
      viewStep === ownerPostCertViewSteps.literalE &&
      !isAccountantFlowOwner
    ) {
      setSavingLiteralE(true);
      try {
        await updateTenant({ literalE: tenantBag.literalE ?? null });
        if (tenantBag.literalE === true && tenantBag.literalEProofFile) {
          await uploadLiteralEProof(tenantBag.literalEProofFile);
        }
        await loadTenant();
        setViewStep(ownerPostCertViewSteps.personalize);
        toast.success(t("onboarding.saved"));
      } catch (err) {
        // Say what the server said. Swallowing it is what turned a plan-gate
        // 403 on the constancia upload into "No se pudo actualizar la empresa",
        // a message that named neither the file nor the reason.
        toast.error(serverMessage(err) ?? t("tenants.edit.errorUpdate"));
      } finally {
        setSavingLiteralE(false);
      }
      return;
    }
    if (viewStep === ownerPostCertViewSteps.personalize && !isAccountantFlowOwner && !isAccountantUser) {
      setSavingLegalName(true);
      try {
        await updateTenant({
          name: tenantBag.legalName.trim() || undefined,
        });
        await loadTenant();
        setViewStep(ownerPostCertViewSteps.migrationOrPlanFirst);
      } catch {
        toast.error(t("tenants.edit.errorUpdate"));
      } finally {
        setSavingLegalName(false);
      }
      return;
    }
    if (
      viewStep === ownerPostCertViewSteps.migrationOrPlanFirst &&
      !isAccountantFlowOwner &&
      tenantBag.isMigrating
    ) {
      setViewStep(ownerPostCertViewSteps.planMigrating);
      return;
    }
    if (
      (viewStep === ownerPostCertViewSteps.migrationOrPlanFirst &&
        !isAccountantFlowOwner &&
        !tenantBag.isMigrating) ||
      (viewStep === ownerPostCertViewSteps.planMigrating &&
        !isAccountantFlowOwner &&
        tenantBag.isMigrating)
    ) {
      if (tenantBag.selectedPlanId) {
        setSavingPlan(true);
        try {
          await persistBenefitDeclarationIfNeeded();
          if (planUsesActivationPath(
            tenantBag.selectedPlanId,
            tenantBag.selectedBillingInterval,
            tenantBag.plans
          )) {
            const res = await updateTenantPlan(
              tenantBag.selectedPlanId,
              tenantBag.selectedBillingInterval,
              "onboarding"
            );
            await loadTenant();
            if (res.redirectUrl) {
              window.location.href = res.redirectUrl;
              return;
            }
            toast.success(t("onboarding.saved"));
            setViewStep(totalSteps);
          } else {
            const res = await createPlanCheckout({
              planId: tenantBag.selectedPlanId,
              billingInterval: tenantBag.selectedBillingInterval,
              returnContext: "onboarding",
            });
            await loadTenant();
            if (res.redirectUrl) {
              window.location.href = res.redirectUrl;
              return;
            }
            toast.success(t("onboarding.saved"));
            setViewStep(totalSteps);
          }
        } catch {
          toast.error(t("onboarding.errorSave"));
        } finally {
          setSavingPlan(false);
        }
      }
      return;
    }
    setViewStep((s) => s + 1);
  }, [
    needsRegimeGate,
    handleRegimeGateContinue,
    needsPlanGate,
    handlePlanGateContinue,
    handleContinueFromDgi,
    viewStep,
    situationStep,
    dgiStep,
    certChoiceStep,
    certContentStep,
    abitabStep,
    situationChoice,
    certificateAcquisitionChoice,
    certificateRequestPayload,
    currentCertificateRequest?.status,
    step,
    isAccountantFlowOwner,
    isAccountantUser,
    accountantStepsComplete,    tenantBag.legalName,
    tenantBag.selectedPlanId,
    tenantBag.selectedBillingInterval,
    tenantBag.literalE,
    tenantBag.literalEProofFile,
    tenantBag.isMigrating,
    tenant?.planId,
    tenant?.id,
    tenant?.hasDgiCredentials,
    tenant?.step6AbitabAcknowledgedAt,
    tenant?.companyType,
    currentCertificateRequest?.idDigitalRequestNumber,
    step6NotarialCanAdvance,
    loadTenant,
    saveProfile,
    updateTenant,
    updateTenantPlan,
    createPlanCheckout,
    setSavingFiscal,
    setSavingLiteralE,
    setSavingPlan,
    t,
    totalSteps,
    queryClient,
    needsCfeImportStep,
    ownerPostCertViewSteps,
  ]);

  // Onboarding situation funnel: resolve the already_issuer vs regularization
  // split for "ya facturé antes" against DGI's emisor padrón. The backend fails
  // soft to already_issuer; this also swallows transport errors the same way so
  // the funnel never dead-ends on a flaky call.
  const handleDetectEmisorStatus = useCallback(async (): Promise<
    "already_issuer" | "issuer_need_regularization"
  > => {
    try {
      return await detectEmisorStatus();
    } catch {
      return "already_issuer";
    }
  }, []);

  // Persist the FINAL resolved situation choice (from the two-question funnel)
  // and branch. Takes the choice explicitly instead of reading situationChoice
  // state, because the "ya facturé antes" branch resolves it via an async
  // detect at submit time — reading state here would race that update.
  const handleSubmitSituation = useCallback(
    async (
      choice: SituationChoice,
      opts?: { migratingFromAnotherPlatform?: boolean },
    ): Promise<void> => {
      setSituationChoice(choice);
      setSavingSituation(true);
      try {
        await updateTenant({
          migratingFromAnotherPlatform:
            opts?.migratingFromAnotherPlatform ?? false,
          onboardingSituationChoice: choice,
        });
        await loadTenant();
        // regularization: cert-first — kick the cert request off before DGI so
        // the async Abitab identity check runs in parallel with DGI auto-flow.
        // scratch + already_issuer: DGI-first (scratch agrees with the
        // resolveStep scratch-pin instead of racing it).
        if (choice === "issuer_need_regularization") {
          setViewStep(certChoiceStep);
        } else {
          setViewStep(dgiStep);
        }
      } catch {
        toast.error(t("onboarding.errorSave"));
      } finally {
        setSavingSituation(false);
      }
    },
    [
      certChoiceStep,
      dgiStep,
      loadTenant,
      setSituationChoice,
      setSavingSituation,
      setViewStep,
      t,
    ],
  );

  const handleBackStep = useCallback(() => {
    setViewStep((prev) => {
      const result = backStep({
        prevViewStep: prev,
        isAccountantFlowOwner,
        isAccountantUser,
        isAlreadyIssuer,
        certBeforeDgi,
        onboardingSituationChoice: effectiveSituation,
        certificateAcquisitionChoice,
        ownerPostCertViewSteps,
        needsCfeImportStep,
        preCertOffset,
      });
      if (result.cfeReset) {
        void updateTenant({ onboardingCfeImportStepCompletedAt: null })
          .then(() => loadTenant())
          .catch(() => undefined);
      }
      return result.step;
    });
  }, [
    setViewStep,
    isAccountantFlowOwner,
    isAccountantUser,
    isAlreadyIssuer,
    certBeforeDgi,
    effectiveSituation,
    certificateAcquisitionChoice,
    ownerPostCertViewSteps,
    needsCfeImportStep,
    preCertOffset,
    loadTenant,
    updateTenant,
  ]);

  const handleSaveProfile = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void saveProfile();
    },
    [saveProfile]
  );

  return {
    ...tenantBag,
    user,
    viewStep,
    viewStepSynced,
    setViewStep,
    situationChoice,
    setSituationChoice,
    dgiPasswordReady,
    setDgiPasswordReady,
    dgiStepsCompletedConfirmed,
    setDgiStepsCompletedConfirmed,
    dgiStep3SuccessToastAcknowledged,
    totalSteps,
    preCertOffset,
    situationStep,
    dgiStep,
    certChoiceStep,
    certContentStep,
    abitabStep,
    isLastStep,
    isAccountantFlowOwner,
    isAccountantUser,    canFinish,
    certificateAcquisitionChoice,
    setCertificateAcquisitionChoice,
    certificateAcquisitionCanAdvance,
    setCertificateAcquisitionCanAdvance,
    step6NotarialCanAdvance,
    setStep6NotarialCanAdvance,
    certificateRequestPayload,
    setCertificateRequestPayload,
    step6FiscaloConfirmPayload,
    markCertWaitContinued,
    currentCertificateRequest,
    currentCertificateRequestLoading,
    certificateStepNextLoading,
    handleConfirmFiscaloCertificate,
    handleRetryCertificateAutomation,
    certificateAutomationRetrying,
    nextButtonDisabled,
    nextButtonLoading,
    finishing,
    handleWhoSetupSubmit,
    handleFinish,
    handleNextStep,
    handleSubmitSituation,
    handleDetectEmisorStatus,
    handleContinueFromDgi,
    handleBackStep,
    handleSkipCfeImportStep,
    markCfeImportUploadCompleted,
    handleSaveProfile,
    navigate,
    dgiConfirmationStatusResolved,
    needsCfeImportStep,
    ownerPostCertViewSteps,
    needsPlanGate,
    needsRegimeGate,
    needsHomologacionPending,
    handlePlanGateContinue,
    handleRegimeGateContinue,
    accountantInviteEmail,
    setAccountantInviteEmail,
    missingOnboardingCaeCfeTypes,
    onboardingCaeListRefreshing,
  };
}

export type OnboardingContext = ReturnType<typeof useOnboarding>;
